import { useEffect, useRef } from 'react'

/**
 * Ambient background for the hero: the 192-unit board as a field of cells,
 * quietly computing. Cells pulse as scores land, and a few ignite the way a
 * burned unit does. Canvas rather than SVG so it stays cheap at 60fps.
 *
 * Decorative only — aria-hidden, and it holds still under reduced motion.
 */
export default function BurnCanvas() {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const COLS = 24
    const ROWS = 8
    const N = COLS * ROWS

    // each cell carries its own phase so the field shimmers rather than blinks
    const phase = new Float32Array(N)
    const speed = new Float32Array(N)
    const burn = new Float32Array(N)
    for (let i = 0; i < N; i++) {
      phase[i] = Math.random() * Math.PI * 2
      speed[i] = 0.4 + Math.random() * 0.8
    }

    let w = 0, h = 0, dpr = 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = rect.width
      h = rect.height
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    let raf = 0
    let t = 0
    let nextIgnite = 0

    const draw = () => {
      ctx.clearRect(0, 0, w, h)

      const gapX = w / COLS
      const gapY = h / ROWS
      const size = Math.min(gapX, gapY) * 0.30

      // occasionally set a cell alight — a unit being spent
      if (!reduced && t > nextIgnite) {
        burn[Math.floor(Math.random() * N)] = 1
        nextIgnite = t + 0.25 + Math.random() * 0.5
      }

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const i = r * COLS + c
          const x = gapX * (c + 0.5)
          const y = gapY * (r + 0.5)

          const pulse = reduced ? 0.5 : (Math.sin(t * speed[i] + phase[i]) + 1) / 2
          const base = 0.05 + pulse * 0.10

          if (burn[i] > 0.001) {
            const b = burn[i]
            ctx.fillStyle = `rgba(235,104,52,${0.10 + b * 0.55})`
            ctx.beginPath()
            ctx.roundRect(x - size / 2, y - size / 2, size, size, 1.5)
            ctx.fill()
            if (b > 0.35) {
              ctx.fillStyle = `rgba(235,104,52,${(b - 0.35) * 0.16})`
              ctx.beginPath()
              ctx.arc(x, y, size * (1 + (1 - b) * 5), 0, Math.PI * 2)
              ctx.fill()
            }
            burn[i] = Math.max(0, b - 0.012)
          } else {
            ctx.fillStyle = `rgba(180,214,190,${base})`
            ctx.beginPath()
            ctx.roundRect(x - size / 2, y - size / 2, size, size, 1.5)
            ctx.fill()
          }
        }
      }

      t += 0.016
      raf = requestAnimationFrame(draw)
    }

    raf = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  return <canvas ref={ref} className="burn-canvas" aria-hidden="true" />
}
